import React, { useState, useEffect } from 'react';
import './ConceptMatchingGame.styles.sass';

function shuffleArray(array) {
  return array.sort(() => Math.random() - 0.5);
}

function ConceptMatchingGame({ conceptsData, meaningsData }) {
  const [shuffledConcepts, setShuffledConcepts] = useState([]);
  const [shuffledMeanings, setShuffledMeanings] = useState([]);
  // Ordem fixa dos chips (mobile) — shuffledConcepts é reordenado a cada acerto pra
  // alinhar com a linha do significado (layout desktop); os chips não devem "pular".
  const [conceptChips, setConceptChips] = useState([]);
  const [matchedConcepts, setMatchedConcepts] = useState({});
  // Toque em vez de arrastar: o drag nativo do HTML5 não funciona em touch (iOS Safari
  // não dispara dragstart/drop por toque), então o jogo travava no celular. No desktop
  // (onde o layout de linha .draggable/.droppable aparece, ver .styles.sass) o arrastar
  // convive de graça com o toque — touch simplesmente não dispara os eventos de drag.
  const [selectedConcept, setSelectedConcept] = useState(null);
  const [showCongrats, setShowCongrats] = useState(false);
  const [shakeConcept, setShakeConcept] = useState(null);
  const [draggingConcept, setDraggingConcept] = useState(null);
  const [dragOverMeaning, setDragOverMeaning] = useState(null);

  useEffect(() => {
    // Shuffle concepts and meanings when the component mounts
    const shuffled = shuffleArray([...conceptsData]);
    setShuffledConcepts(shuffled);
    setConceptChips(shuffled);
    setShuffledMeanings(shuffleArray([...meaningsData]));
  }, []);

  const handleSelectConcept = conceptId => {
    setSelectedConcept(current => (current === conceptId ? null : conceptId));
  };

  const attemptMatch = (conceptId, meaningId) => {
    if (conceptId === meaningId) {
      // Correct match: swap positions so concept and meaning land on the same row
      const conceptIndex = shuffledConcepts.findIndex(concept => concept.id === conceptId);
      const meaningIndex = shuffledMeanings.findIndex(meaning => meaning.id === meaningId);

      const newShuffledConcepts = [...shuffledConcepts];
      const temp = newShuffledConcepts[meaningIndex];
      newShuffledConcepts[meaningIndex] = newShuffledConcepts[conceptIndex];
      newShuffledConcepts[conceptIndex] = temp;

      setShuffledConcepts(newShuffledConcepts);
      setMatchedConcepts(prev => ({ ...prev, [conceptId]: true }));
    } else {
      setShakeConcept(conceptId);
      setTimeout(() => setShakeConcept(null), 500);
    }
  };

  const handleSelectMeaning = meaningId => {
    if (!selectedConcept) return;
    attemptMatch(selectedConcept, meaningId);
    setSelectedConcept(null);
  };

  const handleDrop = (e, meaningId) => {
    e.preventDefault();
    setDragOverMeaning(null);
    const conceptId = e.dataTransfer.getData('text/plain');
    if (conceptId && !matchedConcepts[conceptId]) attemptMatch(conceptId, meaningId);
  };

  useEffect(() => {
    if (Object.keys(matchedConcepts).length === conceptsData.length) {
      setShowCongrats(true);
    }
  }, [matchedConcepts]);

  return (
    <div className="game-container">
      <div className="instructions mb-1 rounded-xl border border-solid border-neutral-100 bg-white px-4 py-2.5 font-semibold text-primary-dark shadow-card">Toque ou arraste um conceito até o significado correspondente</div>

      {/* Mobile: conceitos agrupados em chips compactos (evita empilhar 2 linhas por par) */}
      <div className="concept-chip-row">
        {conceptChips.map(concept => {
          const matched = matchedConcepts[concept.id];
          return (
            <button
              key={concept.id}
              type="button"
              className={`concept-chip ${shakeConcept === concept.id ? 'shake flash-red' : ''} ${matched ? 'concept-chip--matching' : ''} ${selectedConcept === concept.id ? 'concept-chip--selected' : ''}`}
              disabled={matched}
              onClick={() => handleSelectConcept(concept.id)}
            >
              {concept.name}
            </button>
          );
        })}
      </div>

      <div className="game-content">
        {shuffledMeanings.map((meaning, index) => {
          const concept = shuffledConcepts[index];
          const matched = matchedConcepts[concept.id];
          return (
            <div
              key={meaning.id}
              className={matched ? 'concept-meaning-row concept-meaning-row--matching' : 'concept-meaning-row'}
            >
              <span className="concept-meaning-number">{index + 1}</span>
              <div className="concept-wrapper">
                <button
                  type="button"
                  draggable={!matched}
                  onDragStart={e => {
                    e.dataTransfer.setData('text/plain', concept.id);
                    setDraggingConcept(concept.id);
                  }}
                  onDragEnd={() => setDraggingConcept(null)}
                  className={`draggable ${shakeConcept === concept.id ? 'shake flash-red' : ''} ${matched ? 'draggable--matching' : ''} ${selectedConcept === concept.id ? 'draggable--selected' : ''} ${draggingConcept === concept.id ? 'draggable--dragging' : ''}`}
                  disabled={matched}
                  onClick={() => handleSelectConcept(concept.id)}
                >
                  <p>{concept.name}</p>
                </button>
              </div>
              <button
                type="button"
                onDragOver={e => {
                  e.preventDefault();
                  setDragOverMeaning(meaning.id);
                }}
                onDragLeave={() => setDragOverMeaning(current => (current === meaning.id ? null : current))}
                onDrop={e => handleDrop(e, meaning.id)}
                className={`droppable ${matched ? 'droppable--matching' : ''} ${(selectedConcept || dragOverMeaning === meaning.id) && !matched ? 'droppable--targetable' : ''}`}
                disabled={matched}
                onClick={() => handleSelectMeaning(meaning.id)}
              >
                {meaning.meaning}
              </button>
            </div>
          );
        })}
      </div>
      {showCongrats && <div className="congrats-message">Parabéns! Você conectou todos os conceitos corretamente!</div>}
    </div>
  );
}

export default ConceptMatchingGame;
